import {
    Avatar,
    Button,
    Grid,
    Header,
    Modal,
    Popup,
    Row,
    styles,
    Surface,
    Table,
    Text,
    useTheme
} from "tmwx-design-web/src";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "gui-common/redux/store";
import {useCallback, useEffect, useMemo, useState} from "react";
import {usePermissions} from "tmwx-design-common";
import {ColumnDef} from "@tanstack/react-table";
import {getPodList} from "gui-common/redux/action/efe/podAction";
import {useNavigate} from "react-router-dom";
import {isEmpty} from "gui-common/appHelper";
import {setSnackbarData} from "gui-common/redux/action/snackbarAction";
import HandleAddMeasurementLocation from "./HandleAddMeasurementLocation";
import {getATRLocationListAll} from "gui-common/redux/action/efe/efeLocationAction";
import {Company} from "gui-common/redux/data/company";
import {getUnassignedDeviceList} from "gui-common/redux/action/efe/deviceAction";
import {Pod} from "gui-common/redux/data/pod";

const MeasurementLocationList = () => {
    const { t } = useTranslation()
    const { padding, roundness } = useTheme()
    const navigate = useNavigate();
    const dispatch: any = useDispatch();
    const { hasRights } = usePermissions()
    const [selectedPod, setSelectedPod] = useState<Pod | null>(null);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false)
    const [dialogTitle, setDialogTitle] = useState<string>('')
    const [dialogSubtitle, setDialogSubtitle] = useState<string>('')
    const [showLocations, setShowLocations] = useState<boolean>(false)
    const podList = useSelector<RootState, Pod[]>(state => state.pod.podList);
    const podLoading = useSelector<RootState, boolean>(state => state.pod.loading);
    const activeCompany = useSelector<RootState, Company>(state => state.auth.activeCompany);

    const jumpToPodDetails = (machineId: number, podId: string) => {
        const navigationUrl = "/efe/pod/details?id=" + machineId + "&podId=" + podId;
        navigate(navigationUrl);
    }

    const jumpToOpParams = (systemId: string) => {
        const navigationUrl = "/efe/pod/params?systemId=" + systemId;
        navigate(navigationUrl);
    }

    const jumpToCommands = (systemId: string) => {
        const navigationUrl = "/efe/pod/commands?systemId=" + systemId;
        navigate(navigationUrl);
    }

    const jumpToMetrics = (systemId: string, podId: string) => {
        const query = new URLSearchParams({ systemId, podId });
        const navigationUrl = "/efe/pod/metrics?" + query.toString();
        navigate(navigationUrl);
    }

    const jumpToMqttMessages = useCallback((systemId: string, podId: string) => {
        const query = new URLSearchParams({ systemId, podId });
        navigate('/efe/pod/mqtt-messages?' + query.toString());
    }, [navigate]);

    const handleAdd = () => {
        setDialogOpen(true)
        dispatch(getATRLocationListAll(activeCompany.id))
        dispatch(getUnassignedDeviceList(activeCompany.id))
    }

    const closeDialog = () => {
        setSelectedPod(null)
        setDialogOpen(false)
        setShowLocations(false)
        setDialogTitle('')
        setDialogSubtitle('')
        dispatch(getPodList(activeCompany.id))
    }

    const columns = useMemo<ColumnDef<Pod>[]>(
        () => [
            {
                id: 'success',
                type: 'success',
                size: 20,
                cell: ({ row }) => (
                    !isEmpty(row.original.lastMetricTime) ? (
                        < Button
                            iconLeft={'check'}
                            status={'success'}
                            size='small'
                            onPress={() => {
                            }}
                        />
                    ) : (
                        < Popup
                            style={styles.marginRight4}
                            triggerComponent={
                                < Button
                                    iconLeft={'warning'}
                                    status={'warning'}
                                    size='small'
                                    onPress={() => {
                                    }}
                                />
                            }
                            type='hover'
                        >
                            <Surface style={{ padding: padding, minWidth: 250 }} elevation={1}>
                                <Text>{'Huzamosabb ideje nem történt üzenetváltás'}</Text>
                            </Surface>
                        </Popup >
                    )
                ),
                enableColumnOrdering: false,
                enableColumnActions: false,
                enableSorting: false,
                enableResizing: false,
                enableHiding: false,
            },
            {
                accessorKey: 'podId',
                meta: t('efe.measurementLocId'),
                maxSize: 120,
                header: () => <Text bold>{t('efe.measurementLocId')}</Text>,
                cell: ({ row }) => (
                    !isEmpty(row.original.podId) && <Button
                        status='neutral'
                        size='small'
                        uppercase={false}
                        onPress={() => {
                            navigator.clipboard.writeText('' + row.original.podId)
                            /* dispatch(setSnackbarData({
                                status: 'success',
                                text: t('copyToClipboard')
                            })) */
                        }}>{'' + row.original.podId}</Button>
                )
            },
            {
                accessorKey: 'customerName',
                meta: t('name'),
                header: () => <Text bold>{t('name')}</Text>
            },
            {
                accessorKey: 'location.address',
                meta: t('atr.machines.location'),
                header: () => <Text bold>{t('atr.machines.location')}</Text>
            },
            {
                accessorKey: 'systemId',
                meta: t('efe.id'),
                size: 50,
                header: () => <Text bold>{t('efe.id')}</Text>,
                cell: ({ row }) => (
                    !isEmpty(row.original.systemId) && < Button
                        status='neutral'
                        size='small'
                        uppercase={false}
                        onPress={() => {
                            navigator.clipboard.writeText(row.original.systemId)
                            dispatch(setSnackbarData({
                                status: 'success',
                                text: t('copyToClipboard')
                            }))
                        }}>{row.original.systemId}</Button>
                )
            },
            {
                accessorKey: 'lastMetricTime',
                meta: t('atr.machines.lastCommunicationDate'),
                minSize: 70,
                sortingFn: (rowA, rowB, columnId) => {
                    const a = rowA.getValue<string>(columnId)
                    const b = rowB.getValue<string>(columnId)

                    if (!a && !b) return 0
                    if (!a) return -1
                    if (!b) return 1

                    return new Date(a).getTime() - new Date(b).getTime()
                },
                header: () => <Text bold>{t('atr.machines.lastCommunicationDate')}</Text>,
            },
            {
                id: 'actions',
                meta: t('actions'),
                size: 40,
                header: () => <Text bold>{t('details')}</Text>,
                cell: ({ row }) => (
                    <div style={{ ...styles.row }}>
                        {hasRights(['efe_pod_details_view']) && (
                            <Popup
                                style={styles.marginRight4}
                                triggerComponent={
                                    <Button
                                        iconLeft={'table_chart_view_outlined'}
                                        size='small'
                                        onPress={() => jumpToPodDetails(row.original.id, row.original.podId)}
                                    />
                                }
                                type='hover'
                            >
                                <Surface style={{ padding: padding }} elevation={1}>
                                    <Text>{t('details')}</Text>
                                </Surface>
                            </Popup>
                        )}

                        {hasRights(['efe_efe_param_list']) && (
                            <Popup
                                style={styles.marginRight4}
                                triggerComponent={
                                    <Button
                                        iconLeft={'settings'}
                                        size='small'
                                        onPress={() => jumpToOpParams(row.original.systemId)}
                                    />
                                }
                                type='hover'
                            >
                                <Surface style={{ padding: padding }} elevation={1}>
                                    <Text>{t('atr.opParams.opParamsText')}</Text>
                                </Surface>
                            </Popup>
                        )}

                        {hasRights(['efe_efe_command_list']) && (
                            <Popup
                                style={styles.marginRight4}
                                triggerComponent={
                                    <Button
                                        iconLeft={'install_desktop'}
                                        size='small'
                                        onPress={() => jumpToCommands(row.original.systemId)}
                                    />
                                }
                                type='hover'
                            >
                                <Surface style={{ padding: padding }} elevation={1}>
                                    <Text>{t('efe.efeCommands')}</Text>
                                </Surface>
                            </Popup>
                        )}

                        {hasRights(['efe_metrics_list']) && (
                            <Popup
                                style={styles.marginRight4}
                                triggerComponent={
                                        <Button
                                            iconLeft={'dashboard'}
                                            size='small'
                                            onPress={() => jumpToMetrics(row.original.systemId, row.original.podId)}
                                        />
                                    }
                                type='hover'
                            >
                                <Surface style={{ padding: padding }} elevation={1}>
                                    <Text>{t('efe.metrics')}</Text>
                                </Surface>
                            </Popup>
                        )}
                        {hasRights(['efe_mqtt_grab_list']) && (
                            <Popup
                                style={styles.marginRight4}
                                triggerComponent={
                                    <Button
                                        iconLeft={'swap_horiz'}
                                        size='small'
                                        onPress={() => jumpToMqttMessages(row.original.systemId, row.original.podId)}
                                    />
                                }
                                type='hover'
                            >
                                <Surface style={{ padding: padding }} elevation={1}>
                                    <Text>{t('efe.savedMqttMessages')}</Text>
                                </Surface>
                            </Popup>
                        )}
                    </div>
                ),
                enableColumnOrdering: false,
                enableColumnActions: false,
                enableSorting: false,
                enableResizing: false,
                enableHiding: false,
            },
        ],
        [dispatch, hasRights, padding, t, jumpToMqttMessages]
    );

    useEffect(() => {
        if (hasRights(["efe_pod_list"])) {
            dispatch(getPodList(activeCompany.id));
        }
    }, [dispatch, activeCompany])

    return (
        <Surface style={{ height: '100vh', paddingTop: 8, paddingBottom: 8 }}>
            <Grid.Container style={{ borderRadius: roundness }} maxWidth='xl' elevation={0} centered>
                <Grid.Col>
                    <Table
                        id="POD_TABLE"
                        columns={columns}
                        data={podList}
                        loading={podLoading}
                        refreshData={() => dispatch(getPodList(activeCompany.id))}
                        dense
                        useGlobalFilter
                        useColumnVisibility
                        add={hasRights(['efe_pod_create', 'efe_efe_list', 'efe_location_list']) ? () => handleAdd() : undefined}
                        labels={{
                            add: t('add'),
                            deletePreferences: t('deletePreferences'),
                            jumpHere: t('jumpHere'),
                            page: t('page'),
                            refresh: t('refresh'),
                            row: t('row'),
                            rowsSelected: t('rowsSelected'),
                            savePreferencies: t('savePreferences'),
                            searchData: t('searchData'),
                            selectCurrentPageData: t('selectCurrentPageData'),
                            showAll: t('showAll'),
                            sizing: t('sizing')
                        }}
                    />
                </Grid.Col>
            </Grid.Container>

            <Modal
                visible={showLocations}
                type='dialog'
                dialogMaxWidth='xs'
                contentElevation={0}
                closeModal={closeDialog}
                header={(
                    <Header
                        subtitle={dialogSubtitle}
                        title={dialogTitle}
                        actionRight={<Button iconLeft={'clear'} status='neutral' variant='ghost'
                            onPress={closeDialog} />}
                    />
                )}
            >
                {showLocations && (
                    <Surface>
                        {selectedPod && (
                            <Row
                                avatar={<Avatar iconName={'cases_outlined'} />}
                                title={selectedPod.companyName}
                            />
                        )}

                    </Surface>
                )}
            </Modal>

            <Modal
                visible={dialogOpen}
                type='dialog'
                dialogMaxWidth='m'
                contentElevation={0}
                closeModal={closeDialog}
                header={(
                    <Header
                        title={t('add')}
                        actionRight={<Button iconLeft={'clear'} status='neutral' variant='ghost'
                            onPress={closeDialog} />}
                    />
                )}
            >
                <HandleAddMeasurementLocation actionType={'add'} item={selectedPod}
                                              onClose={closeDialog} />
            </Modal>
        </Surface>
    )
}

export default MeasurementLocationList;
